// Netflix-style child switcher chip for the header. Shows the active
// child's avatar + first name; opens a compact popover with every
// child, quick "Add child" and "Manage" links.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, Users, KeyRound } from "lucide-react";
import {
  listChildren,
  getActiveChild,
  getActiveChildId,
  switchToChild,
  childStats,
  TIER_CHOICES,
} from "@/lib/children";

const TIER_TINT = {
  seedlings: "bg-rose-200",
  explorers: "bg-yellow-200",
  pioneers: "bg-emerald-200",
};

export default function ChildSwitcher() {
  const [children, setChildren] = useState(() => listChildren());
  const [active, setActive] = useState(() => getActiveChild());
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const refresh = () => {
      setChildren(listChildren());
      setActive(getActiveChild());
    };
    window.addEventListener("we-children-changed", refresh);
    window.addEventListener("we-active-child-changed", refresh);
    return () => {
      window.removeEventListener("we-children-changed", refresh);
      window.removeEventListener("we-active-child-changed", refresh);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  // Feb 2026 · header-consistency fix — when the family has zero
  // children, keep the ChildSwitcher slot mounted with an "Add child"
  // pill of the same shape. Previously we returned null which caused
  // the header's right cluster to visibly reflow (progress pill
  // sometimes leapt into the empty spot, "quick links" fallbacks
  // appeared inconsistently) between sessions. A stable slot means
  // the profile / quick-link chip lives in exactly the same place on
  // every page load.
  if (children.length < 1) {
    return (
      <Link
        to="/family"
        data-testid="child-switcher-add"
        aria-label="Add a child profile"
        className="shrink-0 inline-flex items-center gap-1.5 h-11 md:h-10 px-2.5 md:px-3 rounded-full border-[3px] border-slate-900 bg-yellow-100 shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-transform"
      >
        <UserPlus size={14} strokeWidth={3} className="text-slate-900" aria-hidden />
        <span className="hidden sm:inline text-xs font-bold text-slate-900">Add child</span>
      </Link>
    );
  }

  const activeId = getActiveChildId();
  const shortName = (n) => String(n || "").split(" ")[0].slice(0, 12);

  return (
    <div ref={rootRef} className="relative shrink-0" data-testid="child-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={active ? `Playing as ${active.name} — switch child` : "Switch child"}
        data-testid="child-switcher-toggle"
        className="group inline-flex items-center gap-1.5 h-11 md:h-10 px-2.5 md:px-3 rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] transition-transform"
      >
        <span
          aria-hidden
          className={`inline-flex items-center justify-center h-7 w-7 rounded-full border-2 border-slate-900 text-lg ${
            active ? TIER_TINT[active.tier] || "bg-yellow-100" : "bg-yellow-100"
          }`}
        >
          {active?.avatar || "🦊"}
        </span>
        <span className="hidden sm:inline font-bold text-sm text-slate-900 max-w-[5ch] xl:max-w-[7ch] truncate">
          {shortName(active?.name) || "Pick"}
        </span>
        <span aria-hidden className="text-slate-500 text-xs">▾</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-72 rounded-2xl border-[3px] border-slate-900 bg-white shadow-[0_6px_0_0_rgba(15,23,42,1)] p-3 z-50"
            role="menu"
            data-testid="child-switcher-menu"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1">
              Who&apos;s playing?
            </p>
            <ul className="mt-2 space-y-1.5">
              {children.map((c) => {
                const stats = childStats(c.id);
                const isActive = c.id === activeId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => { switchToChild(c.id); setOpen(false); }}
                      data-testid={`child-switcher-pick-${c.id}`}
                      className={`w-full flex items-center gap-2 rounded-xl border-2 px-2 py-1.5 text-left transition-colors ${
                        isActive
                          ? "border-slate-900 bg-yellow-50"
                          : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`inline-flex items-center justify-center h-9 w-9 rounded-full border-2 border-slate-900 text-xl ${TIER_TINT[c.tier] || "bg-yellow-100"}`}
                      >
                        {c.avatar || "🦊"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-sm font-bold text-slate-900 truncate">
                          {c.name}
                          {isActive && (
                            <span className="ml-1 align-middle inline-block h-2 w-2 rounded-full bg-emerald-500" aria-label="active" />
                          )}
                        </p>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate">
                          {(TIER_CHOICES.find((t) => t.id === c.tier)?.label) || c.tier} · ⭐ {stats.stars} · 🔥 {stats.streak}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
              <Link
                to="/family"
                onClick={() => setOpen(false)}
                data-testid="child-switcher-add"
                className="inline-flex items-center justify-center gap-1 h-9 rounded-full border-2 border-slate-900 bg-emerald-200 text-xs font-bold hover:bg-emerald-300"
              >
                <UserPlus size={13} strokeWidth={3} /> Add
              </Link>
              <Link
                to="/join"
                onClick={() => setOpen(false)}
                data-testid="child-switcher-enter-code"
                className="inline-flex items-center justify-center gap-1 h-9 rounded-full border-2 border-slate-900 bg-yellow-200 text-xs font-bold hover:bg-yellow-300"
                title="Kid arriving on a new device? Enter their code."
              >
                <KeyRound size={13} strokeWidth={3} /> Code
              </Link>
              <Link
                to="/family"
                onClick={() => setOpen(false)}
                data-testid="child-switcher-manage"
                className="inline-flex items-center justify-center gap-1 h-9 rounded-full border-2 border-slate-900 bg-white text-xs font-bold hover:bg-slate-50"
              >
                <Users size={13} strokeWidth={3} /> Manage
              </Link>
            </div>
            {/* Cycle 63 · Part 14 — subtle "Change age" affordance
                so a growing child can graduate between tiers without
                needing a grown-up. Kept as a small text link so it
                doesn't compete with Add/Code/Manage above. */}
            <Link
              to="/age?change=1"
              onClick={() => setOpen(false)}
              data-testid="child-switcher-change-age"
              className="mt-1.5 block text-center text-[11px] font-bold text-slate-500 hover:text-slate-800 underline"
            >
              Change my age tier
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
